import { useQuery } from "@tanstack/react-query";
import { useMutationWithToast } from "@/hooks/shared/use-mutation-with-toast";

import { api } from "@/lib/api-typed";
import type { BodyOf, Schemas } from "@/types/api";

// ============================================================================
// Tipos — os do backend, gerados do contrato (`src/types/api.gen.ts`)
// ============================================================================

type Certificado = Schemas["app__schemas__transaction_certificate__CertificateRead"];
type CertificadoNovo = Schemas["app__schemas__transaction_certificate__CertificateCreate"];

/** Os referenciais. Um cliente pode exigir um e não outro. */
export type CertificateStandard = Certificado["standard"];

/** Não expira; pode ser revogado. */
export type CertificateStatus = Certificado["status"];

/**
 * O estado de um pedido.
 *
 * `nao_aplicavel` é o que um campo vazio não exprime: sem ele, «não se pede»
 * fica indistinguível de «ainda não pedimos».
 */
export type RequestStatus = Schemas["RequestRead"]["status"];


export type TraceabilityCertificate = Certificado;
export type CertificateInput = CertificadoNovo;

/** Um lote que a regra do prefixo propõe. Propõe — quem decide é uma pessoa. */
export type ProposedLot = Schemas["ProposedLot"];

export type CertificateRequest = Schemas["RequestRead"];
export type RequestInput = Schemas["RequestCreate"];

// Ligar um lote muda a contagem do certificado, a lista de lotes sem documento
// e a fila. Tudo derivado das mesmas tabelas.
const CERT_KEYS = [
  ["certificates"],
  ["certificate-requests"],
  ["certificate-queue"],
  ["lots-without-certificate"],
  ["proposed-lots"],
  ["lot-certificates"],
];

// ============================================================================
// Certificados
// ============================================================================

export interface CertificateFilters {
  procura?: string;
  standard?: CertificateStandard;
  estado?: CertificateStatus;
}

/** `enabled: false` evita o pedido quando o resultado ainda não interessa (p. ex. um seletor noutro separador). */
export const useCertificates = (filters: CertificateFilters = {}, { enabled = true }: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: ["certificates", filters],
    enabled,
    queryFn: () => api.get("/transaction-certificates", { query: filters }),
  });

export const useCreateCertificate = () => {
  return useMutationWithToast({
    mutationFn: (data: CertificateInput) => api.post("/transaction-certificates", { body: data }),
    invalidateQueries: [...CERT_KEYS],
  });
};

export const useUpdateCertificate = () => {
  return useMutationWithToast({
    mutationFn: ({ id, data }: { id: string; data: BodyOf<"/transaction-certificates/{cert_id}", "patch"> }) =>
      api.patch("/transaction-certificates/{cert_id}", { path: { cert_id: id }, body: data }),
    invalidateQueries: [...CERT_KEYS],
  });
};

// ============================================================================
// Ligação aos lotes
// ============================================================================

/** Os lotes que a regra do prefixo propõe. Não liga nada — só propõe. */
export const useProposedLots = (certificateId: string | null) =>
  useQuery({
    queryKey: ["proposed-lots", certificateId],
    enabled: !!certificateId,
    queryFn: () => api.get("/transaction-certificates/{cert_id}/proposed-lots", { path: { cert_id: certificateId! } }),
  });


/** Material que se diz certificado e não tem documento que o prove. */
export const useLotsWithoutCertificate = (apenasReciclado = true) =>
  useQuery({
    queryKey: ["lots-without-certificate", apenasReciclado],
    queryFn: () => api.get("/lots-without-certificate", { query: { apenas_reciclado: apenasReciclado } }),
  });

export const useLinkLot = () => {
  return useMutationWithToast({
    mutationFn: (data: BodyOf<"/lot-certificates", "post">) => api.post("/lot-certificates", { body: data }),
    invalidateQueries: [...CERT_KEYS],
  });
};

// ============================================================================
// Fila de pedidos
// ============================================================================

export const useCertificateRequests = (estado?: RequestStatus) =>
  useQuery({
    queryKey: ["certificate-requests", estado],
    queryFn: () => api.get("/certificate-requests", { query: { estado } }),
  });

export const useQueueSummary = ({ enabled = true }: { enabled?: boolean } = {}) =>
  useQuery({
    queryKey: ["certificate-queue"],
    enabled,
    queryFn: () => api.get("/certificate-requests/summary"),
  });

export const useCreateRequest = () => {
  return useMutationWithToast({
    mutationFn: (data: RequestInput) => api.post("/certificate-requests", { body: data }),
    invalidateQueries: [...CERT_KEYS],
  });
};

export const useUpdateRequest = () => {
  return useMutationWithToast({
    mutationFn: ({ id, data }: { id: string; data: BodyOf<"/certificate-requests/{request_id}", "patch"> }) =>
      api.patch("/certificate-requests/{request_id}", { path: { request_id: id }, body: data }),
    invalidateQueries: [...CERT_KEYS],
  });
};

